import {
  Button,
  ButtonMenu,
  Css,
  DateField,
  documentScrollBodyMinHeight,
  Filters,
  HeaderAction,
  IconButton,
  Loader,
  LoadingSkeleton,
  MenuItem,
  ModalBody,
  ModalFooter,
  ModalHeader,
  pageContentPaddingX,
  PageHeaderLayout,
  RouteTab,
  useModal,
  useTestIds,
  zIndices,
} from "@homebound/beam";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { Location } from "react-router";
import { Route, Routes, useNavigate } from "react-router-dom";
import { SearchBox, useNavigationCheck } from "src/components";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { FeatureFlagType } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { SendBulkTradePartnerAvailabilityRequestEmailModal } from "src/routes/projects/dynamic-schedules/components/SendTradePartnerAvailabilityRequestEmailModal";
import {
  DraftPlanScheduleQueryHookResult,
  useSaveDraftPlanScheduleMutation,
} from "src/routes/projects/dynamic-schedules/draft-mode/ScheduleDraftMode.generated";
import {
  ScheduleHeaderDetails,
  useDraftScheduleHeaderActions,
} from "src/routes/projects/dynamic-schedules/draft-mode/ScheduleDraftModeHeader";
import { DynamicSchedulesMilestoneTable } from "src/routes/projects/dynamic-schedules/milestone-view/DynamicSchedulesMilestone";
import { ProjectParams } from "src/routes/routesDef";
import {
  createDraftCalendarUrl,
  createDraftGanttUrl,
  createDraftMilestoneUrl,
  createDraftScheduleNewTaskUrl,
  createDraftSchedulePublishUrl,
  createDraftScheduleUrl,
  createMilestoneCatalogFormUrl,
} from "src/RouteUrls";
import {
  projectDraftScheduleCalendarRoute,
  projectDraftScheduleGanttRoute,
  projectDraftScheduleMilestoneRoute,
  projectDraftScheduleRoute,
} from "src/routing/domains/projectsPaths";
import { foldEnum, queryResult } from "src/utils";
import { Temporal } from "temporal-polyfill";
import { useProjectContext } from "../../context/ProjectContext";
import {
  DraftScheduleLayoutState,
  TaskGroupBy,
  useDraftScheduleLayoutState,
  useSetLookaheadGroupBy,
} from "../components/draftScheduleFilters";
import { DynamicScheduleView, useScheduleRouteMatch } from "../utils";
import { AddDraftTasks } from "./AddDraftTasks";
import { DraftScheduleCalendar } from "./DraftScheduleCalendar";
import { DraftScheduleGantt } from "./DraftScheduleGantt";
import { useDraftPlanSchedule } from "./DraftScheduleLayout";
import { DraftScheduleTable, filterPlanTasksAndMilestones } from "./DraftScheduleTable";
import { ScheduleDraftModeCalendarDelayFlagModal } from "./ScheduleDraftModeCalendarDelayFlagModal";
import { useDraftScheduleStore } from "./scheduleDraftStore";

/** The draft schedule page. `DraftScheduleLayout` provides its draft. */
export function ScheduleDraftMode() {
  const { projectId } = useRouteParams<ProjectParams>();
  const tid = useTestIds({}, "scheduleDraftMode");
  const { useRegisterNavigationCheck } = useNavigationCheck();

  const draftTaskChanges = useDraftScheduleStore((state) => state.draftTaskChanges);
  const userAddedScheduleFlags = useDraftScheduleStore((state) => state.userAddedScheduleFlags);
  const query = useDraftPlanSchedule();

  const draftModePath = useMemo(() => createDraftScheduleUrl(projectId), [projectId]);

  const draftTasks = useMemo(
    () => query.data?.draftPlanSchedule.planTasks ?? [],
    [query.data?.draftPlanSchedule.planTasks],
  );

  // Prevent navigation if there are unsaved changes UNLESS the user is navigating into a draft mode route
  useRegisterNavigationCheck(
    (currentLocation: Location, nextLocation?: Location | undefined) => {
      return (
        (draftTaskChanges.isEmpty && userAddedScheduleFlags.isEmpty) ||
        (!nextLocation
          ? true
          : nextLocation.pathname.includes(draftModePath) || nextLocation.pathname === currentLocation.pathname)
      );
    },
    [draftTaskChanges, userAddedScheduleFlags, draftModePath],
  );

  return (
    <div {...tid}>
      <Routes>
        <Route index element={<DraftDataViews query={query} scheduleParentId={projectId} />} />
        <Route path="calendar" element={<DraftDataViews query={query} scheduleParentId={projectId} />} />
        <Route path="gantt" element={<DraftDataViews query={query} scheduleParentId={projectId} />} />
        <Route path="lookahead" element={<LookaheadRedirect scheduleParentId={projectId} />} />
        <Route path="milestones" element={<DraftDataViews query={query} scheduleParentId={projectId} />} />
        <Route path="new" element={<AddDraftTasks draftTasks={draftTasks} scheduleParentId={projectId} />} />
      </Routes>
    </div>
  );
}

type DraftDataViewsProps = { query: DraftPlanScheduleQueryHookResult; scheduleParentId: string };

function DraftDataViews(props: DraftDataViewsProps) {
  const { query, scheduleParentId } = props;
  const { name: projectName } = useProjectContext();
  const navigate = useNavigate();
  const [saveDraftSchedule] = useSaveDraftPlanScheduleMutation();

  const draftTaskChanges = useDraftScheduleStore((state) => state.draftTaskChanges);
  const userAddedScheduleFlags = useDraftScheduleStore((state) => state.userAddedScheduleFlags);
  const draftScheduleView = useScheduleRouteMatch();
  const setUserAddedScheduleFlags = useDraftScheduleStore((state) => state.setUserAddedScheduleFlags);
  const removeUserAddedScheduleFlags = useDraftScheduleStore((state) => state.removeUserAddedScheduleFlags);
  const reset = useDraftScheduleStore((state) => state.reset);
  const canSkipPublishFlow = useDraftScheduleStore((state) => state.canSkipPublishFlow);

  const { openModal } = useModal();
  const { layoutState, filter, groupBy, search } = useDraftScheduleLayoutState(draftScheduleView);

  const setIsPublishedMode = useCallback(
    () => navigate(createDraftSchedulePublishUrl(scheduleParentId)),
    [navigate, scheduleParentId],
  );
  const setAddTaskMode = useCallback(() => {
    navigate(createDraftScheduleNewTaskUrl(scheduleParentId));
  }, [navigate, scheduleParentId]);

  const skipStaleTaskAutoPin = useDraftScheduleStore((state) => state.skipStaleTaskAutoPin);
  const currentDate = useDraftScheduleStore((state) => state.currentDate);
  const hasDraftChanges = !draftTaskChanges.isEmpty || !userAddedScheduleFlags.isEmpty || skipStaleTaskAutoPin;

  const publishScheduleAndResetStore = useCallback(async () => {
    await saveDraftSchedule({
      variables: { input: { scheduleParentId, draftTaskChanges, skipStaleTaskAutoPin, currentDate } },
    });
    reset();
    userAddedScheduleFlags.forEach((flag) => {
      if (flag.clientId) {
        removeUserAddedScheduleFlags(flag.clientId);
      }
    });
    // TODO: validate this eslint-disable. It was automatically ignored when the oxlint migration restored `react-hooks/exhaustive-deps` to an error.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, draftTaskChanges, reset, saveDraftSchedule, scheduleParentId, skipStaleTaskAutoPin]);

  const onPublishClick = useCallback(() => {
    const canSkipPublish = canSkipPublishFlow(
      query.data?.draftPlanSchedule.planTasks ?? [],
      query.data?.draftPlanSchedule.newlyCompletedPlanTasks ?? [],
      query.data?.draftPlanSchedule.tradeConfirmPlanTasks ?? [],
    );
    openModal({
      content: (
        <ConfirmationModal
          title="Are you sure you want to Publish?"
          label={canSkipPublish ? "Publish Schedule" : "Enter Publish Workflow"}
          onConfirmAction={canSkipPublish ? publishScheduleAndResetStore : setIsPublishedMode}
          confirmationMessage="Publishing will accept the simulated schedule changes and apply them to the existing schedule. Are you sure you want to continue?"
        />
      ),
    });
  }, [
    canSkipPublishFlow,
    query.data?.draftPlanSchedule.planTasks,
    query.data?.draftPlanSchedule.newlyCompletedPlanTasks,
    query.data?.draftPlanSchedule.tradeConfirmPlanTasks,
    openModal,
    publishScheduleAndResetStore,
    setIsPublishedMode,
  ]);

  // using local state to make sure gantt state is in sync with local state
  const [isCollapsed, setIsCollapsed] = useState(false);

  const handleCollapseExpandToggle = useCallback(() => {
    setIsCollapsed((prevState) => !prevState);
  }, [setIsCollapsed]);

  const showCollapseExpandButton =
    draftScheduleView === DynamicScheduleView.Gantt &&
    (groupBy === TaskGroupBy.CostCode || groupBy === TaskGroupBy.Stage);

  const actionsMenuItems = useDraftScheduleActionsMenuItems({ scheduleParentId, hasDraftChanges });
  const draftHeaderActions = useDraftScheduleHeaderActions({ onPublishClick, loadingDraftChanges: query.loading });

  // The page header only holds the draft's Discard and Publish actions. Everything else lives in the table toolbar.
  const viewMenuItems: MenuItem[] = [
    { label: "Add Task", onClick: setAddTaskMode },
    ...(draftScheduleView === DynamicScheduleView.Milestone
      ? [
          {
            label: "Create Milestone",
            disabled: !draftTaskChanges.isEmpty || !userAddedScheduleFlags.isEmpty,
            onClick: createMilestoneCatalogFormUrl("add"),
          },
        ]
      : []),
    ...(draftScheduleView === DynamicScheduleView.Calendar
      ? [
          {
            label: "Add Delay Flag",
            onClick: () =>
              openModal({
                content: (
                  <ScheduleDraftModeCalendarDelayFlagModal
                    planTasks={query.data?.draftPlanSchedule.planTasks ?? []}
                    setUserAddedScheduleFlags={setUserAddedScheduleFlags}
                  />
                ),
              }),
          },
        ]
      : []),
    ...actionsMenuItems,
  ];

  const tableActions: HeaderAction[] = [
    ...(showCollapseExpandButton
      ? [
          {
            label: isCollapsed ? "Expand All" : "Collapse All",
            variant: "secondary" as const,
            onClick: handleCollapseExpandToggle,
          },
        ]
      : []),
    { kind: "menu", trigger: { icon: "verticalDots", variant: "outline" }, tooltip: "Actions", items: viewMenuItems },
  ];

  // Carry the search between the task views. Each view's search box reads ?search= when it mounts. Milestones only
  // searches milestone names, so it neither gets nor passes on a search.
  const searchQuery =
    search && draftScheduleView !== DynamicScheduleView.Milestone ? `?${new URLSearchParams({ search })}` : "";
  const tabs: RouteTab[] = [
    {
      name: "List",
      href: createDraftScheduleUrl(scheduleParentId) + searchQuery,
      path: projectDraftScheduleRoute.path,
    },
    {
      name: "Calendar",
      href: createDraftCalendarUrl(scheduleParentId) + searchQuery,
      path: projectDraftScheduleCalendarRoute.path,
    },
    {
      name: "Gantt",
      href: createDraftGanttUrl(scheduleParentId) + searchQuery,
      path: projectDraftScheduleGanttRoute.path,
    },
    {
      name: "Milestones",
      href: createDraftMilestoneUrl(scheduleParentId),
      path: projectDraftScheduleMilestoneRoute.path,
    },
  ];

  // Every view but List shows a loader in the header while the draft re-simulates.
  const isSimulating = draftScheduleView !== DynamicScheduleView.List && !!query.previousData && query.loading;

  return (
    <PageHeaderLayout
      pageHeader={{
        title: "Schedule",
        documentTitleSuffix: projectName,
        actions: draftHeaderActions,
        tabs: { tabs },
        rightSlot: isSimulating ? (
          <div css={Css.df.aic.gap1.sm.$}>
            <Loader size="xs" />
            Simulating schedule changes...
          </div>
        ) : undefined,
      }}
    >
      {queryResult(query, {
        data: ({ draftPlanSchedule, enumDetails }) => {
          const { schedulingExclusionDates = [] } = draftPlanSchedule.planSchedule;
          const { filteredTasks, filteredMilestones, defaultMilestones } = filterPlanTasksAndMilestones(
            draftPlanSchedule.planTasks,
            filter,
            search,
          );

          const sharedProps = {
            scheduleParentId,
            loading: query.loading,
            schedulingExclusionDates,
            planTasks: filteredTasks,
            planMilestones: filteredMilestones,
            hasDraftChanges,
          };
          const isChartView =
            draftScheduleView === DynamicScheduleView.Calendar || draftScheduleView === DynamicScheduleView.Gantt;

          return (
            <>
              {/* Z-index: Sit above the table views' sticky toolbar, whose white background would cover the card's shadow */}
              <div css={{ ...Css.layoutContainer.z(zIndices.tableActions + 1).$, ...pageContentPaddingX }}>
                <ScheduleHeaderDetails planSchedule={draftPlanSchedule.planSchedule} />
                {/* The table views get their toolbar from `GridTableLayout` */}
                {isChartView && <DraftScheduleViewToolbar layoutState={layoutState} actions={tableActions} />}
              </div>
              {draftScheduleView === DynamicScheduleView.Milestone ? (
                <DynamicSchedulesMilestoneTable
                  hasDraftChanges={hasDraftChanges}
                  planMilestones={defaultMilestones}
                  projectId={scheduleParentId}
                  layoutState={layoutState}
                  actions={tableActions}
                />
              ) : !isChartView ? (
                <DraftScheduleTable
                  enumDetails={enumDetails}
                  groupBy={groupBy}
                  // We need to pass in all of the plan tasks to calculate the progress while grouping by Stage
                  allTasks={draftPlanSchedule.planTasks}
                  scheduleParent={draftPlanSchedule.planSchedule.parent}
                  layoutState={layoutState}
                  actions={tableActions}
                  {...sharedProps}
                />
              ) : (
                // The page scrolls the stats and toolbar away, then the chart fills the viewport below the page header
                <div css={{ ...Css.layoutContainer.h(documentScrollBodyMinHeight()).pb3.$, ...pageContentPaddingX }}>
                  {foldEnum(draftScheduleView, {
                    Calendar: () => <DraftScheduleCalendar {...sharedProps} />,
                    Gantt: () => <DraftScheduleGantt groupBy={groupBy} {...sharedProps} isCollapsed={isCollapsed} />,
                  })}
                </div>
              )}
            </>
          );
        },
        loading: () => (
          <div css={{ ...pageContentPaddingX, ...Css.pt3.$ }}>
            <LoadingSkeleton columns={4} rows={10} />
          </div>
        ),
      })}
    </PageHeaderLayout>
  );
}

type DraftScheduleViewToolbarProps = { layoutState: DraftScheduleLayoutState; actions: HeaderAction[] };

/**
 * Search, filter, and action controls for the views that are not tables (calendar and gantt).
 *
 * TODO: Beam only renders its table toolbar inside `GridTableLayout`, so this rebuilds it with Beam's `Filters`
 * over the same layout state. Swap it out if Beam exports a standalone toolbar.
 */
function DraftScheduleViewToolbar(props: DraftScheduleViewToolbarProps) {
  const { layoutState, actions } = props;
  const { filter, setFilter, filterDefs, groupBy, setSearchString } = layoutState;

  return (
    <div css={Css.df.jcsb.aic.gap1.pt3.pb2.$}>
      <div css={Css.df.aic.gap1.$}>
        <SearchBox clearable onSearch={setSearchString} />
        {filterDefs && (
          <Filters
            filterDefs={filterDefs}
            filter={filter}
            onChange={setFilter}
            groupBy={groupBy}
            numberOfInlineFilters={1}
          />
        )}
      </div>
      <div css={Css.df.aic.gap1.$}>
        {actions.map((action) =>
          action.kind === "menu" ? (
            <ButtonMenu key="menu" {...action} />
          ) : action.kind === "icon" ? (
            <IconButton key={action.label} {...action} />
          ) : (
            <Button key={action.label} {...action} />
          ),
        )}
      </div>
    </div>
  );
}

type DraftScheduleActionsMenuItemsProps = { scheduleParentId: string; hasDraftChanges: boolean };

function useDraftScheduleActionsMenuItems(props: DraftScheduleActionsMenuItemsProps): MenuItem[] {
  const { scheduleParentId, hasDraftChanges } = props;
  const showTradeComms = useFeatureFlag(FeatureFlagType.DynamicSchedulesTradeComms);
  const { openModal } = useModal();
  const skipStaleTaskAutoPin = useDraftScheduleStore((state) => state.skipStaleTaskAutoPin);
  const setSkipStaleTaskAutoPin = useDraftScheduleStore((state) => state.setSkipStaleTaskAutoPin);
  const setCurrentDate = useDraftScheduleStore((state) => state.setCurrentDate);

  const handleStaleTaskAutoPinToggle = useCallback(() => {
    openModal({
      content: (
        <ScheduleRerunDateModal
          skipStaleTaskAutoPin={skipStaleTaskAutoPin}
          onConfirm={(date) => {
            setSkipStaleTaskAutoPin(!skipStaleTaskAutoPin);
            setCurrentDate(date);
          }}
        />
      ),
    });
  }, [openModal, skipStaleTaskAutoPin, setSkipStaleTaskAutoPin, setCurrentDate]);

  return useMemo(
    () => [
      ...(showTradeComms
        ? [
            {
              disabled: hasDraftChanges && "Save schedule changes first before sending bulk notifications",
              label: "Bulk Send Scheduling Notifications",
              onClick: () =>
                openModal({
                  content: <SendBulkTradePartnerAvailabilityRequestEmailModal scheduleParentId={scheduleParentId} />,
                  size: "xxl",
                }),
            },
          ]
        : []),
      {
        label: `${skipStaleTaskAutoPin ? "Enable" : "Disable"} Stale Task Auto-Pin`,
        onClick: handleStaleTaskAutoPinToggle,
      },
    ],
    [hasDraftChanges, openModal, scheduleParentId, skipStaleTaskAutoPin, handleStaleTaskAutoPinToggle, showTradeComms],
  );
}

/** Lookahead used to be its own tab. Old links now open the list grouped by Lookahead. */
function LookaheadRedirect(props: { scheduleParentId: string }) {
  const { scheduleParentId } = props;
  const navigate = useNavigate();
  const setLookaheadGroupBy = useSetLookaheadGroupBy();
  useEffect(() => {
    setLookaheadGroupBy();
    navigate(createDraftScheduleUrl(scheduleParentId), { replace: true });
  }, [setLookaheadGroupBy, navigate, scheduleParentId]);
  return null;
}

type ScheduleRerunDateModalProps = {
  skipStaleTaskAutoPin: boolean;
  onConfirm: (date: Temporal.PlainDate | undefined) => void;
};

function ScheduleRerunDateModal({ skipStaleTaskAutoPin, onConfirm }: ScheduleRerunDateModalProps) {
  const { closeModal } = useModal();
  const [selectedDate, setSelectedDate] = useState<Temporal.PlainDate | undefined>(undefined);
  const handleConfirm = useCallback(() => {
    onConfirm(selectedDate);
    closeModal();
  }, [selectedDate, onConfirm, closeModal]);

  return (
    <>
      <ModalHeader>{`${skipStaleTaskAutoPin ? "Enable" : "Disable"} Stale Task Auto-Pin`}</ModalHeader>
      <ModalBody>
        <div css={Css.df.fdc.gap2.$}>
          <p css={Css.sm.gray700.$}>
            Select a date for the schedule to rerun from. If no date is selected, the schedule will be rerun from today.
          </p>
          <DateField
            label="Schedule Rerun Date"
            value={selectedDate}
            onChange={(date) => setSelectedDate(date ?? undefined)}
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="tertiary" label="Cancel" onClick={closeModal} />
        <Button label="Confirm" onClick={handleConfirm} />
      </ModalFooter>
    </>
  );
}
